import React from 'react'
import PropTypes from 'prop-types'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import CircularLoading from '../CircularLoading'
import { groupByGroupSequence, getBidWeightLabel, BidMode } from '../../scenes/BidSession/helpers'
import CIConstants from '../../data/constants'
import { useLanguage } from '../../context/LanguageContext'

const BidSessionsList = ({
  loading = false,
  bidSessions = [],
  selectedBidSessionId = null,
  bidPreferences = [],
  combinedGroups = [],
  selectedCombinedPreferencesId = '',
  selectedGroupId = null,
  groupPreferences = [],
  reserveLineNames = '',
  loadingSmall = false,
  loadingImportPreviousBids = false,
  forceExpandable = false,
  onSelectBidSession = () => {},
  onSelectCombined = () => {},
  onSelectGroup = () => {}
}) => {
  const { labels } = useLanguage();
  const IMPORT_PREVIOUS_BID = labels.IMPORT_PREVIOUS_BID || {};
  const RESERVE_DOWN = labels.RESERVE_DOWN || {};

  const renderScope = (scopeTitle, filterFn, keyPrefix) => {
    const displayedGroupNames = new Set();
    const filteredPreferences = bidPreferences.filter(filterFn);
    if (filteredPreferences.length === 0) return null;
    return (
      <div className="bid-preferences-scope">
        <div className="section-heading">{scopeTitle}</div>
        {filteredPreferences.map((bidPreference, index) => {
          const isCombined = bidPreference.groupName && bidPreference.groupName.trim() !== "";
          const shouldDisplayGroupName = isCombined && !displayedGroupNames.has(bidPreference.groupName);
          if (shouldDisplayGroupName) displayedGroupNames.add(bidPreference.groupName);
          const shouldDisplayItem = !isCombined || (isCombined && shouldDisplayGroupName);

          return shouldDisplayItem && (
            <div key={`bid_preference_${keyPrefix}_${index}`} className={'bid-preferences-list-item'}>
              <div
                className={
                  isCombined
                    ? `bid-preferences-list-item-name combined ${bidPreference.importable ? 'importable' : 'not-importable'}`
                    : `bid-preferences-list-item-name ${bidPreference.importable ? 'importable' : 'not-importable'}`
                }
                onClick={isCombined ? () => onSelectCombined(bidPreference.groupName) : () => { }}
              >
                {bidPreference.groupName || bidPreference.description}{bidPreference.scoreApplicable ? ` --- ${bidPreference.optionLevel !== CIConstants.RESERVE_DOWN_TO.levelNonReserveDownTo ? `${RESERVE_DOWN.RESERVE_DOWN_TO_LABEL} ${bidPreference.optionLevel === CIConstants.RESERVE_DOWN_TO.levelAny ? RESERVE_DOWN.ANY : bidPreference.optionLevel - 1}` : bidPreference.bidMode === BidMode.AUTOMATIC.biddingMode ? getBidWeightLabel(bidPreference.score) : bidPreference.score}` : ''}
              </div>
              {isCombined && (
                <div
                  className={
                    selectedCombinedPreferencesId === bidPreference.groupName
                      ? 'bid-preferences-list-item groups-list show'
                      : 'bid-preferences-list-item groups-list hide'
                  }
                >
                  {combinedGroups?.map((group, groupIdx) => {
                    let showGroup = selectedGroupId === group.groupSequence
                      && selectedCombinedPreferencesId === bidPreference.groupName;
                    const groupColorValue = groupColor(group.preferences);
                    return (
                      <div
                        onClick={() => onSelectGroup(group.groupSequence)}
                        key={`group_${keyPrefix}_${groupIdx}`}
                        className="group-container"
                        style={{ color: groupColorValue }}
                      >
                        <span className='bid-preferences-list-item group-button'>{`Group ${group.groupSequence}`}</span>
                        <div className={`bid-preferences-list-item preferences-list${showGroup ? ' show' : ' hide'}`}>
                          {groupPreferences?.map((pref, prefIdx) => (
                            <span
                              key={`preference_${keyPrefix}_${prefIdx}`}
                              className={pref.importable ? 'importable' : 'not-importable'}
                            >
                              {pref.description}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const groupColor = (group) => {
    if (group.every(pref => pref.importable)) {
      return 'green'
    }
    else
      return 'red'
  }

  return (
    <div className='reports-container'>
      {loading ? (
        <CircularLoading />
      ) : (
        bidSessions?.map((bidSession, index) => {
          const detailsDisplay = selectedBidSessionId === bidSession.bidSessionId ? 'block' : 'none';
          const isExpanded = selectedBidSessionId === bidSession.bidSessionId;
          const showOtherOptions = selectedBidSessionId === bidSession.bidSessionId && bidSession.otherOptionDtos && bidSession.otherOptionDtos.length > 0;

          // Compute scope render results
          const regularScope = renderScope(
            IMPORT_PREVIOUS_BID.REGULAR_SCOPE,
            pref => pref.preferenceOptionPropertyId === CIConstants.BID_TYPES.REGULAR,
            "regular"
          );

          const reserveScope = renderScope(
            IMPORT_PREVIOUS_BID.RESERVE_SCOPE,
            pref => pref.preferenceOptionPropertyId === CIConstants.BID_TYPES.RESERVE,
            "reserve"
          );

          // Reserve line names element
          const reserveLineNamesEl = reserveLineNames && reserveLineNames.length > 0 && (
            <div>
              <div className="section-heading">{IMPORT_PREVIOUS_BID.RESERVE_LINE_TYPE_ORDER}</div>
              <div className="section-content">{reserveLineNames}</div>
            </div>
          );

          // Other options content
          const otherOptionsContent = showOtherOptions ? (
            <div className="other-options-section">
              <div className="section-heading">{IMPORT_PREVIOUS_BID.OTHER_OPTIONS_TITLE}</div>
              <div className="section-content">
                {bidSession.otherOptionDtos.map((option, idx) => (
                  <div key={`other_option_${idx}`} className='bid-preferences-list-item'>
                    <span className={`bid-preferences-list-item-name ${option.importable ? 'importable' : 'not-importable'}`}>
                      {option.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          const hasRegular = !!regularScope;
          const hasReserveScope = !!reserveScope || !!reserveLineNamesEl;
          const hasOther = !!otherOptionsContent;

          let leftColumn = null;
          let rightColumn = null;

          if (hasRegular) {
            leftColumn = (
              <div>
                {regularScope}
                {hasOther && otherOptionsContent}
              </div>
            );
            if (hasReserveScope) {
              rightColumn = (
                <div>
                  {reserveScope}
                  {reserveLineNamesEl}
                </div>
              );
            }
          } else if (hasReserveScope) {
            leftColumn = (
              <div
                className="reserve-block"
                style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                {reserveScope}
                {reserveLineNamesEl}
                {hasOther && otherOptionsContent}
              </div>
            );
          } else if (hasOther) {
            leftColumn = otherOptionsContent;
          }

          return (
            <div key={`prev_session_${index}`}>
              <div
                className={index % 2 === 0 ? 'report-item bc-white' : 'report-item bc-trans'}
                onClick={(forceExpandable || bidSession.importable) ? () => onSelectBidSession(bidSession.bidSessionId) : () => { }}
                style={{ cursor: (forceExpandable || bidSession.importable) ? 'pointer' : 'default' }}
              >
                <div className="report-session-row">
                  <div className="report-session-header">
                    <span className="report-card-title">{bidSession.bidSessionName}</span>
                    {(forceExpandable || bidSession.importable) && (
                      <KeyboardArrowDownIcon className={`report-card-arrow${isExpanded ? ' expanded' : ''}`} />
                    )}
                  </div>
                  <div className="report-card-subtitle">
                    {bidSession.importable ? IMPORT_PREVIOUS_BID.IMPORTABLE : IMPORT_PREVIOUS_BID.NOT_IMPORTABLE}
                  </div>
                </div>
              </div>

              <div style={{ display: detailsDisplay }} className='bid-preferences-list'>
                {(loadingSmall || loadingImportPreviousBids) && <CircularLoading big={false} />}
                {!loadingSmall && (
                  <div className='bid-preferences-columns'>
                    {leftColumn && <div className='bid-preferences-column'>{leftColumn}</div>}
                    {rightColumn && <div className='bid-preferences-column'>{rightColumn}</div>}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  )
}

BidSessionsList.propTypes = {
  loading: PropTypes.bool,
  bidSessions: PropTypes.array,
  selectedBidSessionId: PropTypes.number,
  bidPreferences: PropTypes.array,
  combinedGroups: PropTypes.array,
  selectedCombinedPreferencesId: PropTypes.string,
  selectedGroupId: PropTypes.number,
  groupPreferences: PropTypes.array,
  reserveLineNames: PropTypes.string,
  loadingSmall: PropTypes.bool,
  loadingImportPreviousBids: PropTypes.bool,
  forceExpandable: PropTypes.bool,
  onSelectBidSession: PropTypes.func,
  onSelectCombined: PropTypes.func,
  onSelectGroup: PropTypes.func
}

export default BidSessionsList